import { useT } from '@barel/i18n';
import { Banner, Button, Card, FieldAssembly, Select, Spinner } from '@barel/ui';

import { useLinkedinAccountsList } from '../lib/useLinkedinAccountsList';
import { SectionHeader } from './SectionHeader';
import type { SetField } from './types';

export interface AccountBindingSectionProps {
  linkedinAccountId: string | null;
  onChange: SetField;
}

/**
 * Keeps the business/account relationship visible and editable. The API is
 * authoritative for rebinding policy and rejects changes while campaigns are
 * attached, so this selector never tries to infer that policy from stale
 * client-side campaign data.
 */
export function AccountBindingSection({ linkedinAccountId, onChange }: AccountBindingSectionProps) {
  const t = useT('businesses');
  const accounts = useLinkedinAccountsList();
  const currentAccountAvailable = accounts.data.some((account) => account.id === linkedinAccountId);

  return (
    <Card padding="lg" radius="2xl" data-testid="business-section-account">
      <SectionHeader title={t('account.title')} subtitle={t('account.subtitle')} />

      {accounts.isError ? (
        <Banner
          tone="error"
          data-testid="business-account-error"
          action={
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={accounts.refetch}
              data-testid="business-account-retry"
            >
              {t('account.retry')}
            </Button>
          }
        >
          {t('account.error')}
        </Banner>
      ) : accounts.isLoading ? (
        <div
          className="flex items-center gap-2 text-sm text-secondary"
          data-testid="business-account-loading"
        >
          <Spinner size="sm" />
          <span>{t('account.loading')}</span>
        </div>
      ) : accounts.data.length === 0 ? (
        <Banner tone="warning" data-testid="business-account-empty">
          {t('account.empty')}
        </Banner>
      ) : (
        <div className="flex flex-col gap-3">
          {linkedinAccountId && !currentAccountAvailable ? (
            <Banner tone="warning" data-testid="business-account-unavailable">
              {t('account.unavailable')}
            </Banner>
          ) : null}
          <FieldAssembly label={t('account.label')} hint={t('account.hint')} required>
            <Select
              value={currentAccountAvailable ? (linkedinAccountId ?? '') : ''}
              onValueChange={(value) => onChange('linkedinAccountId', value)}
            >
              <Select.Trigger aria-label={t('account.label')} data-testid="business-account-select">
                <Select.Value placeholder={t('account.placeholder')} />
              </Select.Trigger>
              <Select.Portal>
                <Select.Content>
                  {accounts.data.map((account) => (
                    <Select.Item key={account.id} value={account.id}>
                      {account.name}
                    </Select.Item>
                  ))}
                </Select.Content>
              </Select.Portal>
            </Select>
          </FieldAssembly>
        </div>
      )}
    </Card>
  );
}
